// components/BusDeliveryStatus.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  AccessTime,
  DirectionsBus,
  ArrowForward,
  CheckCircle,
  LocalShipping,
  Route,
  ConfirmationNumber
} from "@mui/icons-material";
import moment from "moment";

// Bus company data
const busCompanies = [
  {
    value: "OB",
    label: "OnniBus",
    photoUrl:
      "https://www.onnibus.com/media/22b98dac-0ac0-495c-a9f9-2b0c6d4a38ac",
  },
  {
    value: "KOSONEN",
    label: "Matka-Kosonen",
    photoUrl:"https://matka-kosonen.fi/wp-content/uploads/2023/02/Logo-.png",
  },
  {
    value: "SOISALO",
    label: "Soisalon liikenne",
    photoUrl:
      "https://www.soisalonliikenne.fi/wp-content/uploads/2021/01/banner_logo_w400_h95.png",
  },
  {
    value: "SAVONLINJA",
    label: "Savonlinja",
    photoUrl:
      "https://savonlinja.fi/wp-content/themes/wp-bootstrap-child/assets/images/logo.svg",
  },
  {
    value: "POHJOLAN",
    label: "Pohjolan Liikenne",
    photoUrl:
      "https://933545127e.clvaw-cdnwnd.com/02eb0280155a9207b1e1ad90cc9463b6/200000426-be7c1be7c5/pl-www-2022-logo-white.webp?ph=933545127e",
  },
  {
    value: "KOIVISTO",
    label: "Koiviston Auto",
    photoUrl:
      "https://www.koivistonauto.fi/wp-content/themes/basicteema/img/koiviston_auto_logo.png",
  },
  {
    value: "PEKOLA",
    label: "Pekolan Liikenne",
    photoUrl:
      "https://www.pekolanliikenne.fi/wp-content/uploads/2021/08/PekolanLiikenne-logosiivet_200px-e1632858881982.png",
  },
  { value: "OTHER", label: "Muu bussiyhtiö" },
];

const BusDeliveryStatus = ({ statusData, onStatusChange }) => {
  const [successMessage, setSuccessMessage] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Find bus company info
  const busCompanyInfo = statusData.busCompany
    ? busCompanies.find((company) => company.value === statusData.busCompany)
    : null;

  // Status configuration for button display logic
  const canMarkAsPicked =
    statusData.status !== "picked" && statusData.status !== "completed";

  // Handle marking as picked
  const handleMarkAsPicked = () => {
    if (onStatusChange) {
      setIsUpdating(true);
      onStatusChange(statusData.id)
        .then(() => {
          setSuccessMessage("Paketti merkitty noudetuksi");
          setTimeout(() => setSuccessMessage(null), 3000);
        })
        .finally(() => {
          setIsUpdating(false);
        });
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, marginBottom: 2 }}>
      {/* Success notification */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontWeight: "bold", color: "#1976d2" }}
      >
        Toimituksen tila
      </Typography>

      {/* Bus Company Section */}
      <Box mb={2}>
        <Box display="flex" alignItems="center" mb={1.5} pl={1}>
          <DirectionsBus color="primary" sx={{ mr: 1.5 }} />
          <Typography variant="subtitle1" fontWeight="medium">
            <strong>Bussiyhtiö:</strong>{" "}
            {busCompanyInfo?.label || "Tuntematon bussiyhtiö"}
          </Typography>
        </Box>

        {/* Journey Details */}
        <Box display="flex" alignItems="center" mb={1.5} pl={1}>
            <Route color="action" sx={{ mr: 1.5 }} />


          <Typography variant="body1">
            <strong>Matka:</strong> {statusData.from}{" "}
            <ArrowForward sx={{ mx: 0.5, fontSize: 16 }} /> {statusData.to}
          </Typography>
        </Box>
        {statusData.busExtraInfo && <Box display="flex" alignItems="center" mb={1.5} pl={1}>
            <ConfirmationNumber color="action" sx={{ mr: 1.5 }} />
          <Typography variant="body1">
            <strong>Tarkentavaa tietoa:</strong> {statusData.busExtraInfo}{" "}
          </Typography>
        </Box>}

        {/* Last Updated */}
        <Box display="flex" alignItems="center" mb={1.5} pl={1}>
          <AccessTime sx={{ mr: 1 }} color="action" />
          <Typography variant="body2" color="text.secondary">
            Päivitetty: {moment(statusData.updated).format("HH:mm DD.MM.YYYY")}
          </Typography>
        </Box>
      </Box>

      {/* Status Indicator */}
      <Box
        py={1.5}
        px={2}
        mb={2.5}
        sx={{
          backgroundColor:
            statusData.status === "completed"
              ? "#e8f5e9"
              : statusData.status === "picked"
              ? "#e3f2fd"
              : "#fff3e0",
          borderRadius: 1,
          border: "1px solid",
          borderColor:
            statusData.status === "completed"
              ? "#a5d6a7"
              : statusData.status === "picked"
              ? "#90caf9"
              : "#ffe0b2",
        }}
      >
        <Box display="flex" alignItems="center">
          {statusData.status === "completed" ? (
            <CheckCircle color="success" sx={{ mr: 1.5 }} />
          ) : statusData.status === "picked" ? (
            <DirectionsBus color="primary" sx={{ mr: 1.5 }} />
          ) : (
            <DirectionsBus color="warning" sx={{ mr: 1.5 }} />
          )}
          <Typography variant="body1" fontWeight="medium">
            {statusData.status === "completed"
              ? "Toimitus valmis"
              : statusData.status === "picked"
              ? "Paketti bussissa"
              : "Matkalla"}
          </Typography>
        </Box>
      </Box>

      {/* Bus Company Image (when available) */}
      {busCompanyInfo?.photoUrl && (
        <Box
          display="flex"
          justifyContent="center"
          mb={3}
          sx={{
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid #e0e0e0",
            padding: 2,
            backgroundColor: "#f5f5f5",
          }}
        >
          <img
            src={busCompanyInfo.photoUrl}
            alt={busCompanyInfo.label}
            style={{
              maxWidth: "100%",
              maxHeight: "140px",
              objectFit: "contain",
            }}
          />
        </Box>
      )}

      <Divider sx={{ mb: 2.5 }} />

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" gap={2}>
        {canMarkAsPicked && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<CheckCircle />}
            onClick={handleMarkAsPicked}
            disabled={isUpdating}
          >
            {isUpdating ? "Päivitetään..." : "Merkitse noudetuksi"}
          </Button>
        )}

        {/* When delivery is already picked, show a disabled button */}
        {statusData.status === "picked" && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled
            startIcon={<LocalShipping />}
          >
            Paketti noudettu
          </Button>
        )}

        {/* When delivery is completed, show a disabled button */}
        {statusData.status === "completed" && (
          <Button
            variant="contained"
            color="success"
            fullWidth
            disabled
            startIcon={<CheckCircle />}
          >
            Toimitus valmis
          </Button>
        )}
      </Box>
      
    </Paper>
  );
};

export default BusDeliveryStatus;
